import type { PriorityCollapser } from '@app/components/priority-collapse/createPriorityCollapser';
import CaretDownIcon from '@icon/system/regular/caret-down.svg';
import CircleDashedEmpty from '@icon/system/regular/circle-dashed.svg';
import FilterIcon from '@icon/system/regular/funnel-simple.svg';
import PencilIcon from '@icon/system/regular/pencil-simple.svg';
import PlusIcon from '@icon/system/regular/plus.svg';
import { useInFlightEntityPropertyOptions } from '@queries/properties/in-flight-options';
import { EntityType } from '@service-properties/generated/schemas/entityType';
import type { SoupProperty } from '@service-storage/generated/schemas/soupProperty';
import { Key } from '@solid-primitives/keyed';
import {
  type BadgeSize,
  type BadgeVariant,
  Button,
  badgeTriggerClasses,
  cn,
  HoverCard,
  Layer,
} from '@ui';
import {
  createEffect,
  createMemo,
  createSignal,
  createUniqueId,
  For,
  Match,
  onCleanup,
  Show,
  Switch,
} from 'solid-js';
import { TagDot } from './TagDot';
import { type EditableTag, TagEditorDialog } from './TagEditorDialog';
import { TagPicker, TagSheetPicker } from './TagPicker';
import { TagPill } from './TagPill';
import { useDocTags, useSoupDocTags } from './useDocTags';
import { type ResolvedTag, useSoupResolvedTags } from './useSoupResolvedTags';

type DocTags = ReturnType<typeof useSoupDocTags>;
type CreateDocTags = () => DocTags;

const DEFAULT_MAX_VISIBLE = 3;
const MAX_OVERFLOW_DOTS = 3;

const hoverMenuLabelClass = 'min-w-0 max-w-[30ch] truncate';
const hoverMenuIconButtonClass = 'size-5 shrink-0 p-0.5 text-ink-extra-muted';

function HoverTagRow(props: {
  tag: ResolvedTag;
  onFilter?: (id: string) => void;
  onEdit: (tag: ResolvedTag) => void;
}) {
  return (
    <div class="flex min-w-0 items-center gap-1 whitespace-nowrap rounded-md px-1.5 py-1 text-ink hover:bg-hover">
      <span class="flex min-w-0 flex-1 items-center gap-1.5">
        <TagDot color={props.tag.color} />
        <span class={hoverMenuLabelClass}>{props.tag.label}</span>
      </span>
      <Show when={props.onFilter}>
        {(onFilter) => (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            tooltip="Filter by tag"
            aria-label={`Filter by ${props.tag.label}`}
            class={hoverMenuIconButtonClass}
            onMouseDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onFilter()(props.tag.optionId);
            }}
          >
            <FilterIcon class="size-3.5" />
          </Button>
        )}
      </Show>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        tooltip="Edit tag"
        aria-label={`Edit ${props.tag.label}`}
        class={hoverMenuIconButtonClass}
        onMouseDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          props.onEdit(props.tag);
        }}
      >
        <PencilIcon class="size-3.5" />
      </Button>
    </div>
  );
}

function TagHoverContent(props: {
  tag: ResolvedTag;
  onFilterByTag?: (id: string) => void;
  onEdit: (tag: ResolvedTag) => void;
}) {
  return (
    <div class="flex flex-col gap-0.5 text-ink">
      <Show
        when={props.onFilterByTag}
        fallback={
          <span class="flex min-w-0 items-center gap-1.5 whitespace-nowrap rounded-md px-2 py-1.5">
            <TagDot color={props.tag.color} />
            <span class="min-w-0 truncate">{props.tag.label}</span>
          </span>
        }
      >
        {(onFilterByTag) => (
          <button
            type="button"
            class="flex w-full min-w-0 items-center gap-2 whitespace-nowrap rounded-md px-2 py-1.5 text-left hover:bg-hover"
            onClick={() => onFilterByTag()(props.tag.optionId)}
          >
            <FilterIcon class="size-3.5 shrink-0 text-ink-muted" />
            <span class={hoverMenuLabelClass}>
              Filter by <span class="font-medium">{props.tag.label}</span>
            </span>
          </button>
        )}
      </Show>
      <button
        type="button"
        aria-label={`Edit ${props.tag.label}`}
        class="flex w-full min-w-0 items-center gap-2 whitespace-nowrap rounded-md px-2 py-1.5 text-left hover:bg-hover"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          props.onEdit(props.tag);
        }}
      >
        <PencilIcon class="size-3.5 shrink-0 text-ink-muted" />
        <span class={hoverMenuLabelClass}>
          Edit <span class="font-medium">{props.tag.label}</span>
        </span>
      </button>
    </div>
  );
}

function editableTagFromResolved(
  docTags: DocTags,
  tag: ResolvedTag
): EditableTag | undefined {
  const set = docTags.tagSets().find((tagSet) => tagSet.scope === tag.scope);
  const option = set?.options.find(
    (candidate) => candidate.id === tag.optionId
  );
  if (!set?.definition || !option) return undefined;

  return {
    scope: tag.scope,
    propertyDefinitionId: set.definition.id,
    option,
  };
}

function RowTagEditorOwner(props: {
  tag: ResolvedTag;
  createDocTags: CreateDocTags;
  onClose: () => void;
}) {
  // Editing is an interaction boundary: initialize query/mutation-backed tag
  // state only after the user asks to edit this row's tag.
  const docTags = props.createDocTags();
  const editable = () => editableTagFromResolved(docTags, props.tag);

  return (
    <Show when={editable()}>
      {(tag) => (
        <TagEditorDialog
          open
          mode={{ type: 'edit', tag: tag() }}
          teamAvailable={Boolean(
            docTags.tagSets().some((set) => set.scope === 'team')
          )}
          onClose={props.onClose}
        />
      )}
    </Show>
  );
}

function TagChip(props: {
  tag: ResolvedTag;
  createDocTags: CreateDocTags;
  onFilterByTag?: (id: string) => void;
  onEdit: (tag: ResolvedTag) => void;
  withClickBlock: boolean;
  onActiveChange: (active: boolean) => void;
}) {
  const [pickerActive, setPickerActive] = createSignal(false);
  const onActiveChange = (active: boolean) => {
    setPickerActive(active);
    props.onActiveChange(active);
  };
  onCleanup(() => {
    if (pickerActive()) props.onActiveChange(false);
  });
  return (
    <Layer depth={2}>
      <HoverCard
        placement="bottom-start"
        disabled={pickerActive()}
        content={
          <TagHoverContent
            tag={props.tag}
            onFilterByTag={props.onFilterByTag}
            onEdit={props.onEdit}
          />
        }
      >
        <TagPill
          tag={props.tag}
          createDocTags={props.createDocTags}
          class="max-w-[14ch]"
          dotClass="size-2"
          onActiveChange={onActiveChange}
          withClickBlock={props.withClickBlock}
        />
      </HoverCard>
    </Layer>
  );
}

function TagOverflow(props: {
  tags: ResolvedTag[];
  visibleCount: number;
  createDocTags: CreateDocTags;
  onFilterByTag?: (id: string) => void;
  onEdit: (tag: ResolvedTag) => void;
  withClickBlock: boolean;
  onActiveChange: (active: boolean) => void;
}) {
  const [pickerActive, setPickerActive] = createSignal(false);
  const onActiveChange = (active: boolean) => {
    setPickerActive(active);
    props.onActiveChange(active);
  };
  onCleanup(() => {
    if (pickerActive()) props.onActiveChange(false);
  });
  const dots = () => props.tags.slice(0, MAX_OVERFLOW_DOTS);
  const count = () =>
    `${props.visibleCount > 0 ? '+' : ''}${props.tags.length} ${props.tags.length === 1 ? 'tag' : 'tags'}`;

  return (
    <Layer depth={2}>
      <HoverCard
        placement="bottom-end"
        disabled={pickerActive()}
        content={
          <div class="flex flex-col gap-0.5">
            <For each={props.tags}>
              {(tag) => (
                <HoverTagRow
                  tag={tag}
                  onFilter={props.onFilterByTag}
                  onEdit={props.onEdit}
                />
              )}
            </For>
          </div>
        }
      >
        <TagPicker
          createDocTags={props.createDocTags}
          triggerClass={badgeTriggerClasses({
            variant: 'outline',
            size: 'xs',
            class: 'max-w-[14ch] gap-1.5 touch:min-h-0',
          })}
          triggerLabel="Edit tags"
          onActiveChange={onActiveChange}
          withClickBlock={props.withClickBlock}
        >
          <span class="flex items-center">
            <For each={dots()}>
              {(tag, index) => (
                <TagDot
                  color={tag.color}
                  class={cn('size-2 ring ring-surface', index() > 0 && '-ml-1')}
                />
              )}
            </For>
          </span>
          <span>{count()}</span>
        </TagPicker>
      </HoverCard>
    </Layer>
  );
}

/**
 * Compact tag display for list rows, rendered from the entity's already-loaded
 * soup properties (no per-row fetch). A sole tag keeps its named chip; otherwise
 * shows up to `maxVisible` chips then a "+N tags" chip, or "N tags" when every tag
 * is collapsed. Clicking a chip opens the TagPicker to edit the entity's tags.
 * Hovering a chip surfaces a "Filter by" link; hovering the overflow lists hidden
 * tags. onFilterByTag applies a tag filter to the list.
 */
export function EntityRowTags(props: {
  entityId: string;
  entityType: EntityType;
  properties: SoupProperty[] | undefined;
  maxVisible?: number;
  /** The host's measured overflow controller; hide trailing tags before truncating its title. */
  collapse?: PriorityCollapser;
  class?: string;
  onFilterByTag?: (optionId: string) => void;
}) {
  const appliedTags = useSoupResolvedTags(
    () => props.properties,
    useInFlightEntityPropertyOptions(props.entityId)
  );
  const createDocTags = () =>
    useSoupDocTags(props.entityId, props.entityType, () => props.properties);
  const [pickerActive, setPickerActive] = createSignal(false);
  const [activeChipId, setActiveChipId] = createSignal<string>();
  const [visibleLimit, setVisibleLimit] = createSignal(Infinity);
  const id = createUniqueId();
  const limit = createMemo(() =>
    Math.min(appliedTags().length, props.maxVisible ?? DEFAULT_MAX_VISIBLE)
  );
  const maxVisible = () => {
    if (appliedTags().length === 1) return 1;
    return props.collapse ? Math.min(limit(), visibleLimit()) : limit();
  };

  createEffect(() => {
    const collapse = props.collapse;
    const count = limit();
    // Keep triggers mounted through the picker and any nested tag editor.
    if (!collapse || pickerActive() || appliedTags().length === 1) return;
    for (let index = 0; index < count; index++) {
      onCleanup(
        collapse.register({
          id: `${id}-${index}`,
          priority: -index,
          collapsed: () => maxVisible() <= index,
          setCollapsed: (collapsed) =>
            setVisibleLimit((current) =>
              collapsed
                ? Math.min(current, index)
                : Math.max(current, index + 1)
            ),
        })
      );
    }
  });
  const visible = () => {
    const tags = appliedTags();
    const count = maxVisible();
    const activeId = activeChipId();
    if (
      !activeId ||
      count === 0 ||
      !tags.some((tag) => tag.optionId === activeId)
    ) {
      return tags.slice(0, count);
    }
    const selected = new Set(
      tags
        .filter((tag) => tag.optionId !== activeId)
        .slice(0, Math.max(0, count - 1))
        .map((tag) => tag.optionId)
    );
    selected.add(activeId);
    return tags.filter((tag) => selected.has(tag.optionId));
  };
  const hidden = () => {
    const visibleIds = new Set(visible().map((tag) => tag.optionId));
    return appliedTags().filter((tag) => !visibleIds.has(tag.optionId));
  };
  const [editingTag, setEditingTag] = createSignal<ResolvedTag>();

  return (
    <Show when={appliedTags().length > 0}>
      <div
        class={cn(
          'flex items-center gap-1',
          props.collapse !== undefined && 'shrink-0',
          props.class
        )}
        onClick={(event) => event.stopPropagation()}
      >
        <Key each={visible()} by="optionId">
          {(tag) => (
            <TagChip
              tag={tag()}
              createDocTags={createDocTags}
              onFilterByTag={props.onFilterByTag}
              onEdit={setEditingTag}
              onActiveChange={(active) => {
                setActiveChipId(active ? tag().optionId : undefined);
                setPickerActive(active);
              }}
              withClickBlock
            />
          )}
        </Key>
        <Show when={hidden().length > 0}>
          <TagOverflow
            tags={hidden()}
            visibleCount={visible().length}
            createDocTags={createDocTags}
            onFilterByTag={props.onFilterByTag}
            onEdit={setEditingTag}
            onActiveChange={setPickerActive}
            withClickBlock
          />
        </Show>
        <Show when={editingTag()}>
          {(tag) => (
            <RowTagEditorOwner
              tag={tag()}
              createDocTags={createDocTags}
              onClose={() => setEditingTag(undefined)}
            />
          )}
        </Show>
      </div>
    </Show>
  );
}

type InlineTagsPillProps = {
  docTags: DocTags;
  class?: string;
  variant?: BadgeVariant;
  size?: BadgeSize;
  showPlaceholder?: boolean;
  showAddButton?: boolean;
  /**
   * Fires when the tag picker session (popover or its editor dialog) opens or
   * closes. Callers that hide the pill once it has no tags should collapse on
   * the close rather than mid-interaction, which would tear down the open menu.
   */
  onActiveChange?: (active: boolean) => void;
};

/** The applied tags as dots and a label, shared by both tag pills. */
function InlineTagsPillFace(props: {
  tags: ResolvedTag[];
  showAddButton?: boolean;
}) {
  const first = () => props.tags[0];
  const dots = () => props.tags.slice(0, MAX_OVERFLOW_DOTS);
  const label = () =>
    `${props.tags.length} ${props.tags.length === 1 ? 'Tag' : 'Tags'}`;
  return (
    <>
      <span class="inline-flex min-w-0 items-center gap-1">
        <Switch>
          <Match when={props.tags.length === 0 && props.showAddButton}>
            <PlusIcon class="size-3 shrink-0" />
            <span>Add tags</span>
          </Match>
          <Match when={props.tags.length === 0}>
            <span class="inline-flex min-w-0 items-center gap-1 opacity-50">
              <CircleDashedEmpty class="size-3 shrink-0" />
              <span class="min-w-0 truncate @max-2xl/u-list:hidden">Tags</span>
            </span>
          </Match>
          <Match when={props.tags.length === 1 && first()}>
            {(tag) => (
              <>
                <TagDot color={tag().color} class="size-2.5" />
                <span class="min-w-0 truncate @max-2xl/u-list:hidden">
                  {tag().label}
                </span>
              </>
            )}
          </Match>
          <Match when={props.tags.length > 1}>
            <span class="flex items-center">
              <For each={dots()}>
                {(tag, index) => (
                  <TagDot
                    color={tag.color}
                    class={cn(
                      'size-2.5 ring-2 ring-surface',
                      index() > 0 && '-ml-1'
                    )}
                  />
                )}
              </For>
            </span>
            <span class="min-w-0 truncate @max-2xl/u-list:hidden">
              {label()}
            </span>
          </Match>
        </Switch>
      </span>
      <Show when={props.tags.length > 0 || !props.showAddButton}>
        <CaretDownIcon class="size-3 shrink-0 @max-2xl/u-list:hidden" />
      </Show>
    </>
  );
}

function inlineTagsTriggerClass(props: InlineTagsPillProps, count: number) {
  return badgeTriggerClasses({
    variant: props.variant ?? 'outline',
    size: props.size ?? 'xs',
    class: cn(
      // Capped so a long tag label truncates instead of squeezing
      // whatever shares the row with the pill (e.g. a task title).
      'min-w-0 max-w-35 gap-1.5 text-left touch:min-h-0',
      count === 0 && !props.showAddButton && 'text-ink-extra-muted',
      props.class
    ),
  });
}

function inlineTagsTriggerLabel(props: InlineTagsPillProps, count: number) {
  return count === 0 && props.showAddButton
    ? 'Add tags'
    : 'Change or select tags';
}

export function InlineTagsPill(props: InlineTagsPillProps) {
  const tags = () => props.docTags.appliedTags();
  return (
    <Show
      when={tags().length > 0 || props.showPlaceholder || props.showAddButton}
    >
      <Layer depth={2}>
        <TagPicker
          docTags={props.docTags}
          onActiveChange={props.onActiveChange}
          triggerClass={inlineTagsTriggerClass(props, tags().length)}
          triggerLabel={inlineTagsTriggerLabel(props, tags().length)}
        >
          <InlineTagsPillFace
            tags={tags()}
            showAddButton={props.showAddButton}
          />
        </TagPicker>
      </Layer>
    </Show>
  );
}

/** `InlineTagsPill` for a touch sheet: it picks tags in a stacked sheet. */
export function InlineTagsSheetPill(props: InlineTagsPillProps) {
  const tags = () => props.docTags.appliedTags();
  return (
    <Show
      when={tags().length > 0 || props.showPlaceholder || props.showAddButton}
    >
      <Layer depth={2}>
        <TagSheetPicker
          docTags={props.docTags}
          onActiveChange={props.onActiveChange}
          triggerClass={inlineTagsTriggerClass(props, tags().length)}
          triggerLabel={inlineTagsTriggerLabel(props, tags().length)}
        >
          <InlineTagsPillFace
            tags={tags()}
            showAddButton={props.showAddButton}
          />
        </TagSheetPicker>
      </Layer>
    </Show>
  );
}

export function InlineEntityTagsPill(props: {
  entityId: string;
  entityType: EntityType;
  properties: SoupProperty[] | undefined;
  class?: string;
}) {
  const docTags = useSoupDocTags(
    props.entityId,
    props.entityType,
    () => props.properties
  );
  return (
    <InlineTagsPill
      docTags={docTags}
      class={props.class}
      showPlaceholder={props.entityType === EntityType.TASK}
    />
  );
}

export function InlineFetchedEntityTagsPill(props: {
  entityId: string;
  entityType: EntityType;
  class?: string;
  variant?: BadgeVariant;
  size?: BadgeSize;
  showAddButton?: boolean;
}) {
  const docTags = useDocTags(props.entityId, props.entityType);
  return (
    <InlineTagsPill
      docTags={docTags}
      class={props.class}
      variant={props.variant}
      size={props.size}
      showPlaceholder={props.entityType === EntityType.TASK}
      showAddButton={props.showAddButton}
    />
  );
}
